import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Heart, Eye, Play, TrendingUp, ArrowRight, ShieldCheck, Globe, Wallet, Instagram, MousePointerClick, UserCheck, Rocket, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Navbar } from "@/components/customer/Navbar";
import { Footer } from "@/components/customer/Footer";
import { api } from "@/lib/api";
import { formatGs, formatNumber } from "@/lib/format";
import { discountPct } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";

const ICONS = { users: Users, heart: Heart, eye: Eye, play: Play, "trending-up": TrendingUp };

const FAQS = [
  { q: "¿Necesito entregar mi contraseña de Instagram?", a: "No. Nunca solicitamos tu contraseña. Solo necesitamos tu nombre de usuario público (@tuusuario)." },
  { q: "¿Cómo realizo un pedido?", a: "Elegí el servicio, ingresá tu usuario, seleccioná la cantidad y realizá el pago adjuntando tu comprobante. Todo en pocos pasos." },
  { q: "¿Puedo comprar desde mi celular?", a: "Sí. La plataforma está totalmente optimizada para dispositivos móviles. Podés comprar en menos de un minuto." },
  { q: "¿Cómo puedo consultar mi pedido?", a: "Utilizá el número de pedido o tu WhatsApp/email en la sección 'Consultar pedido' del menú." },
  { q: "¿Los pagos son en guaraníes?", a: "Sí. Todos los precios y pagos son en guaraníes (₲), pensados para el mercado paraguayo." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" } }),
};

const Home = () => {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();
  const s = useSettings();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const minPrice = (svc) => {
    const prices = (svc.packages || []).map((p) => p.price);
    return prices.length ? Math.min(...prices) : 0;
  };

  const cheapestPkg = (svc) => {
    const pkgs = svc.packages || [];
    return pkgs.length ? pkgs.reduce((a, b) => (a.price <= b.price ? a : b)) : null;
  };

  return (
    <div className="App bg-white overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section id="inicio" className="relative pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-tr from-pink-200/40 via-fuchsia-200/30 to-indigo-200/40 rounded-full blur-3xl -z-0" />
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div initial="hidden" animate="show" variants={fade}>
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-3 py-1.5 mb-5">
              <Rocket className="h-3.5 w-3.5" /> {s.brand_slogan}
            </span>
            <h1 className="font-heading font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {s.hero_title}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg">
              {s.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => document.getElementById("servicios")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-ver-servicios"
                className="rounded-full h-13 px-7 py-6 text-base bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-lg shadow-indigo-500/30 active:scale-95 transition-all"
              >
                Ver servicios <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => document.getElementById("como-funciona")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-como-funciona"
                variant="outline"
                className="rounded-full h-13 px-7 py-6 text-base border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold active:scale-95 transition-all"
              >
                ¿Cómo funciona?
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Proceso 100% online</span>
              <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4 text-indigo-500" /> Atención en español</span>
              <span className="inline-flex items-center gap-1.5"><Wallet className="h-4 w-4 text-pink-500" /> Pagos en guaraníes</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: "easeOut" }} className="relative">
            <div className="relative rounded-3xl overflow-hidden soft-shadow-lg">
              <img src="https://images.unsplash.com/photo-1595986630530-969786b19b4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHx5b3VuZyUyMHBlcnNvbiUyMHNtaWxpbmclMjBzbWFydHBob25lfGVufDB8fHx8MTc4OTUyNjc0Nnww&ixlib=rb-4.1.0&q=85" alt="Joven usando Instagram" className="w-full h-[420px] object-cover" />
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="absolute -bottom-5 -left-3 sm:left-6 bg-white rounded-2xl soft-shadow p-4 flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-tr from-pink-500 to-indigo-600 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">+10.000</p>
                <p className="text-xs text-slate-500">seguidores entregados</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section id="servicios" className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-900">{s.services_title}</h2>
            <p className="mt-3 text-slate-600 text-base">{s.services_subtitle}</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((svc, i) => {
              const Icon = ICONS[svc.icon] || TrendingUp;
              const cheap = cheapestPkg(svc);
              const pct = cheap ? discountPct(cheap.price, cheap.original_price) : 0;
              return (
                <motion.div
                  key={svc.id}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, margin: "-50px" }}
                  variants={fade}
                  className="group relative bg-white rounded-3xl p-7 soft-shadow border border-slate-100 hover:-translate-y-1 hover:soft-shadow-lg transition-all duration-300 flex flex-col"
                  data-testid={`service-card-${svc.slug}`}
                >
                  {pct > 0 && (
                    <span className="absolute top-4 right-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">-{pct}%</span>
                  )}
                  <div className="h-12 w-12 rounded-2xl bg-gradient-to-tr from-pink-500/10 to-indigo-500/10 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-900">{svc.name}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{svc.description}</p>
                  <div className="mt-5">
                    <p className="text-xs text-slate-400">Desde</p>
                    {pct > 0 && <p className="text-sm text-slate-400 line-through leading-none">{formatGs(cheap.original_price)}</p>}
                    <p className="font-heading font-extrabold text-2xl text-slate-900">{formatGs(minPrice(svc))}</p>
                  </div>
                  <Button
                    onClick={() => navigate(`/checkout/${svc.slug}`)}
                    data-testid={`buy-btn-${svc.slug}`}
                    className="mt-5 w-full rounded-xl bg-slate-900 hover:bg-indigo-600 text-white font-semibold h-11 active:scale-95 transition-all"
                  >
                    Comprar
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="py-20">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-900">{s.howto_title}</h2>
            <p className="mt-3 text-slate-600">{s.howto_subtitle}</p>
          </div>
          <div className="grid gap-8 md:grid-cols-4">
            {[
              { icon: MousePointerClick, t: "Elegí tu servicio", d: "Seleccioná seguidores, likes, visualizaciones o reels." },
              { icon: UserCheck, t: "Ingresá tu usuario", d: "Solo tu @usuario. Nunca tu contraseña." },
              { icon: Wallet, t: "Realizá el pago", d: "Pagá en guaraníes y adjuntá tu comprobante." },
              { icon: Rocket, t: "¡Listo!", d: "Recibís tu número de pedido y seguimos el proceso." },
            ].map((step, i) => (
              <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="text-center">
                <div className="relative mx-auto mb-5 h-16 w-16 rounded-2xl bg-indigo-50 flex items-center justify-center">
                  <step.icon className="h-7 w-7 text-indigo-600" />
                  <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                </div>
                <h3 className="font-heading font-bold text-slate-900">{step.t}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{step.d}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 mx-auto max-w-2xl rounded-2xl bg-emerald-50 border border-emerald-100 p-5 flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-800"><strong>Nunca te pediremos tu contraseña de Instagram.</strong> Solo necesitamos tu usuario público para impulsar tu perfil de forma segura.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-5">
          <div className="text-center mb-10">
            <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-900">{s.faq_title}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl border border-slate-100 px-5 soft-shadow">
                <AccordionTrigger className="text-left font-semibold text-slate-800 hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-5">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-8 py-14 text-center">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-gradient-to-tr from-pink-500/30 to-indigo-500/30 rounded-full blur-3xl" />
            <Instagram className="h-10 w-10 text-white/80 mx-auto mb-4" />
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white tracking-tight relative">{s.cta_title}</h2>
            <p className="mt-3 text-slate-300 relative">{s.cta_subtitle}</p>
            <Button
              onClick={() => document.getElementById("servicios")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-7 rounded-full h-13 px-8 py-6 text-base bg-white text-slate-900 hover:bg-indigo-50 font-semibold active:scale-95 transition-all relative"
              data-testid="cta-comprar"
            >
              Comprar ahora <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
